import { useEffect, useState } from 'react';
import { Loader2, Search, ShieldAlert, ShieldCheck, Users } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export default function AdminMembers() {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [updating, setUpdating] = useState(null);

  const load = () => {
    api
      .get('/admin/members')
      .then(({ data }) => setMembers(data))
      .catch(() => toast.error('No se pudieron cargar los socios'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const toggleStatus = async (member) => {
    const status = member.status === 'active' ? 'suspended' : 'active';
    setUpdating(member.user_id);
    try {
      await api.patch(`/admin/members/${member.user_id}`, { status });
      toast.success(status === 'active' ? 'Socio reactivado.' : 'Socio suspendido.');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo actualizar al socio.');
    } finally {
      setUpdating(null);
    }
  };

  const filtered = members.filter(
    (m) =>
      m.name?.toLowerCase().includes(search.toLowerCase()) ||
      m.email?.toLowerCase().includes(search.toLowerCase()) ||
      m.member_number?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="admin-members-page">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-orange-500">Padrón</p>
          <h1 className="mt-1 flex items-center gap-2 font-heading text-2xl font-extrabold text-slate-900 sm:text-3xl">
            <Users className="h-7 w-7 text-cnib-blue" /> Socios CNIB
          </h1>
        </div>
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar socio…"
            className="w-full rounded-full border border-slate-200 bg-white py-2.5 pl-11 pr-4 text-sm outline-none transition-all focus:border-cnib-blue focus:ring-2 focus:ring-cnib-blue/15 sm:w-72"
            data-testid="admin-members-search"
          />
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-cnib-blue" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-left text-sm" data-testid="admin-member-table">
              <thead className="border-b border-slate-100 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-5 py-3.5">Socio</th>
                  <th className="px-5 py-3.5">N° socio</th>
                  <th className="px-5 py-3.5">Categoría</th>
                  <th className="px-5 py-3.5">Ciudad</th>
                  <th className="px-5 py-3.5">Estado</th>
                  <th className="px-5 py-3.5 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((m) => (
                  <tr key={m.user_id} className="transition-colors hover:bg-slate-50">
                    <td className="px-5 py-4">
                      <p className="font-bold text-slate-800">{m.name}</p>
                      <p className="text-xs text-slate-500">{m.email}</p>
                    </td>
                    <td className="px-5 py-4 font-mono text-xs font-bold text-cnib-blue">{m.member_number || '—'}</td>
                    <td className="px-5 py-4 text-slate-600">{m.category || '—'}</td>
                    <td className="px-5 py-4 text-slate-600">{m.city || '—'}</td>
                    <td className="px-5 py-4">
                      <span
                        className={`rounded-full px-3 py-1 text-xs font-bold ${
                          m.status === 'active' ? 'bg-emerald-500/10 text-emerald-600' : 'bg-red-500/10 text-red-600'
                        }`}
                      >
                        {m.status === 'active' ? 'Vigente' : 'Suspendido'}
                      </span>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <button
                        onClick={() => toggleStatus(m)}
                        disabled={updating === m.user_id}
                        data-testid={`admin-member-toggle-${m.user_id}`}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-bold transition-colors disabled:opacity-50 ${
                          m.status === 'active'
                            ? 'bg-red-500/10 text-red-600 hover:bg-red-500/20'
                            : 'bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/20'
                        }`}
                      >
                        {m.status === 'active' ? (
                          <><ShieldAlert className="h-3.5 w-3.5" /> Suspender</>
                        ) : (
                          <><ShieldCheck className="h-3.5 w-3.5" /> Reactivar</>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {filtered.length === 0 && (
              <p className="py-12 text-center text-sm text-slate-500">No hay socios que coincidan.</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
